import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, PlayCircle, MapPin, Image as ImageIcon, ChevronLeft, ChevronRight, Video } from "lucide-react";
import PageHero from "../components/PageHero";
import { API_URL, getMediaUrl } from "../config";
import useDynamicContent from "../hooks/useDynamicContent";

const getEmbedUrl = (url, autoplay = true, loop = true, controls = false) => {
  if (!url) return null;
  
  let videoId = "";
  if (url.includes('youtube.com/watch?v=')) {
    videoId = url.split('v=')[1].split('&')[0];
  } else if (url.includes('youtube.com/shorts/')) {
    videoId = url.split('shorts/')[1].split('?')[0].split('&')[0];
  } else if (url.includes('youtu.be/')) {
    videoId = url.split('youtu.be/')[1].split('?')[0];
  }
  
  if (videoId) {
    let params = `autoplay=${autoplay ? 1 : 0}&mute=${autoplay ? 1 : 0}`;
    if (loop) params += `&loop=1&playlist=${videoId}`;
    if (!controls) params += `&controls=0`;
    params += `&modestbranding=1&rel=0`;
    return `https://www.youtube.com/embed/${videoId}?${params}`;
  }
  
  if (url.includes('instagram.com/p/') || url.includes('instagram.com/reel/')) {
    const cleanUrl = url.split('?')[0].replace(/\/$/, "");
    return `${cleanUrl}/embed`;
  }
  if (url.includes('facebook.com') || url.includes('fb.watch')) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0&width=560`;
  }
  return url;
};

const isDirectVideo = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return lowerUrl.endsWith('.mp4') || lowerUrl.endsWith('.webm') || lowerUrl.endsWith('.ogg') || lowerUrl.includes('/uploads/') || lowerUrl.includes('/backend/uploads/');
};

export default function ProjectsPage({ status }) {
  const { content } = useDynamicContent('projects');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(null);
  
  // Dynamic page content
  const isCompleted = status === "completed";
  const ongoingTitle = content.ongoing_title || "Building the future, today.";
  const ongoingDesc = content.ongoing_description || "Explore the ambitious developments currently taking shape under our management.";
  const completedTitle = content.completed_title || "Watch our visions come to life.";
  const completedDesc = content.completed_description || "Browse our 15-25 second video snippets showcasing the premium spaces we've successfully delivered.";

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/projects?status=${status}`);
        setProjects(res.data);
      } catch (err) {
        console.error("Failed to load projects", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [status]);

  return (
    <main data-testid="projects-page" className="bg-[#0a0a0c]">
      <PageHero
        eyebrow={isCompleted ? (content.completed_eyebrow || "Completed Projects") : (content.ongoing_eyebrow || "Ongoing Projects")}
        title={isCompleted ? completedTitle : ongoingTitle}
        description={isCompleted ? completedDesc : ongoingDesc}
        image={isCompleted 
          ? "https://images.unsplash.com/photo-1598990891357-4ef3cd1f0f6d?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85"
          : "https://images.unsplash.com/photo-1541888081273-df26c596b63f?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85"
        }
        breadcrumbs={["Home", isCompleted ? "Completed Projects" : "Ongoing Projects"]}
      />

      <section className="relative pb-32 px-6 md:px-12 pt-12">
        <div className="max-w-7xl mx-auto">
          {loading ? (
             <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
             </div>
          ) : projects.length === 0 ? (
             <div className="text-center text-white/50 py-20 border border-white/10 border-dashed rounded-3xl">
                No {status} projects found.
             </div>
          ) : (
            <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <AnimatePresence>
                {projects.map((p, i) => {
                  const allVideos = [];
                  const seenUrls = new Set();
                  if (p.video_url) {
                    allVideos.push({ url: p.video_url, platform: 'other' });
                    seenUrls.add(p.video_url);
                  }
                  if (p.video_urls && p.video_urls.length > 0) {
                    p.video_urls.forEach(v => {
                      if (v.url && !seenUrls.has(v.url)) {
                        allVideos.push(v);
                        seenUrls.add(v.url);
                      }
                    });
                  }
                  
                  const hasVideos = allVideos.length > 0;
                  const hasGallery = p.gallery_images && p.gallery_images.length > 0;
                  
                  return (
                    <motion.div
                      layout
                      key={p.id}
                      initial={{ opacity: 0, scale: 0.96, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.96, y: 20 }}
                      transition={{ duration: 0.5, delay: i * 0.1 }}
                      className="group relative overflow-hidden rounded-3xl border border-white/10 text-left aspect-[4/5] bg-neutral-900 cursor-pointer"
                      onClick={() => setActiveProject({ ...p, allVideos })}
                    >
                      {hasVideos && isDirectVideo(allVideos[0].url) ? (
                        <video 
                          src={getMediaUrl(allVideos[0].url) + "#t=0.1"}
                          preload="metadata"
                          playsInline
                          muted
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <img 
                          src={p.thumbnail_url || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80"} 
                          alt={p.title} 
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" 
                        />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
                      
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                         <div className="w-16 h-16 rounded-full bg-amber-500/90 text-black flex items-center justify-center shadow-2xl shadow-amber-500/20 backdrop-blur-sm">
                            <span className="font-bold text-sm tracking-widest uppercase">View</span>
                         </div>
                      </div>

                      <div className="absolute inset-x-0 bottom-0 p-8 flex flex-col justify-end pointer-events-none">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                           <span className="px-3 py-1 bg-amber-500/20 text-amber-500 text-[10px] md:text-xs font-bold tracking-wider uppercase rounded-full backdrop-blur-md">
                             {status}
                           </span>
                           {hasVideos && (
                             <span className="px-3 py-1 bg-white/10 text-white text-[10px] md:text-xs font-medium rounded-full backdrop-blur-md flex items-center gap-1">
                               <Video size={12} /> {allVideos.length} Video{allVideos.length > 1 ? 's' : ''}
                             </span>
                           )}
                           {hasGallery && (
                             <span className="px-3 py-1 bg-white/10 text-white text-[10px] md:text-xs font-medium rounded-full backdrop-blur-md flex items-center gap-1">
                               <ImageIcon size={12} /> {p.gallery_images.length} Image{p.gallery_images.length > 1 ? 's' : ''}
                             </span>
                           )}
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl text-white mb-2">{p.title}</h3>
                        {p.description && (
                          <p className="text-white/60 text-sm line-clamp-2 leading-relaxed mb-4">{p.description}</p>
                        )}
                      </div>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>

      {/* Project Detail Modal */}
      <AnimatePresence>
        {activeProject && (
          <ProjectModal project={activeProject} onClose={() => setActiveProject(null)} />
        )}
      </AnimatePresence>
    </main>
  );
}

function ProjectModal({ project, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);

  const hasVideos = project.allVideos.length > 0;
  const hasGallery = project.gallery_images && project.gallery_images.length > 0;

  // Decide initial tab based on available media
  useEffect(() => {
    if (hasVideos) setActiveTab('videos');
    else if (hasGallery) setActiveTab('gallery');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex flex-col p-4 md:p-8"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-50 h-12 w-12 rounded-full flex items-center justify-center bg-white/10 text-white hover:bg-amber-500 hover:text-black transition-colors backdrop-blur-md"
      >
        <X size={20} />
      </button>

      <div className="max-w-6xl w-full mx-auto flex flex-col h-full bg-[#0a0a0c] border border-white/10 rounded-3xl overflow-hidden shadow-2xl relative">
        
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-white/5 shrink-0 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2 text-amber-500 text-sm font-medium">
              <span className="uppercase tracking-wider text-xs">{project.status}</span>
            </div>
            <h2 className="font-display text-3xl md:text-5xl text-white">{project.title}</h2>
          </div>

          <div className="flex gap-2 bg-white/5 p-1 rounded-xl">
            {hasVideos && (
              <button 
                onClick={() => { setActiveTab('videos'); setActiveMediaIndex(0); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${activeTab === 'videos' ? 'bg-amber-500 text-black' : 'text-white/60 hover:text-white'}`}
              >
                <Video size={16} /> Videos ({project.allVideos.length})
              </button>
            )}
            {hasGallery && (
              <button 
                onClick={() => { setActiveTab('gallery'); setActiveMediaIndex(0); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition ${activeTab === 'gallery' ? 'bg-amber-500 text-black' : 'text-white/60 hover:text-white'}`}
              >
                <ImageIcon size={16} /> Gallery ({project.gallery_images.length})
              </button>
            )}
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeTab === 'overview' ? 'bg-amber-500 text-black' : 'text-white/60 hover:text-white'}`}
            >
              Overview
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto relative bg-black/50">
          
          {/* VIDEOS TAB */}
          {activeTab === 'videos' && project.allVideos.length > 0 && (
            <div className="h-full flex flex-col items-center justify-center p-4">
              <div className="w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden border border-white/10 relative shadow-2xl">
                {isDirectVideo(project.allVideos[activeMediaIndex].url) ? (
                  <video 
                    key={project.allVideos[activeMediaIndex].url}
                    src={getMediaUrl(project.allVideos[activeMediaIndex].url)} 
                    controls 
                    className="absolute inset-0 w-full h-full object-contain"
                  />
                ) : (
                  <iframe 
                    key={project.allVideos[activeMediaIndex].url}
                    src={getEmbedUrl(project.allVideos[activeMediaIndex].url, false, false, true)} 
                    className="absolute inset-0 w-full h-full border-none"
                    allow="encrypted-media; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                )}
              </div>
              
              {/* Media Controls */}
              {project.allVideos.length > 1 && (
                <div className="flex items-center gap-4 mt-8">
                  <button 
                    onClick={() => setActiveMediaIndex(prev => prev > 0 ? prev - 1 : project.allVideos.length - 1)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-amber-500 hover:text-amber-500 transition"
                  >
                    <ChevronLeft />
                  </button>
                  <span className="text-white/60 font-medium tracking-widest text-sm">
                    {activeMediaIndex + 1} / {project.allVideos.length}
                  </span>
                  <button 
                    onClick={() => setActiveMediaIndex(prev => prev < project.allVideos.length - 1 ? prev + 1 : 0)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-amber-500 hover:text-amber-500 transition"
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* GALLERY TAB */}
          {activeTab === 'gallery' && project.gallery_images && project.gallery_images.length > 0 && (
            <div className="h-full flex flex-col items-center justify-center p-4">
              <div className="w-full max-w-5xl h-[60vh] md:h-[70vh] rounded-2xl overflow-hidden relative border border-white/10 shadow-2xl">
                <img 
                  key={project.gallery_images[activeMediaIndex]}
                  src={project.gallery_images[activeMediaIndex]} 
                  alt={`${project.title} - Image ${activeMediaIndex + 1}`}
                  className="w-full h-full object-contain bg-black"
                />
              </div>

              {/* Media Controls */}
              {project.gallery_images.length > 1 && (
                <div className="flex items-center gap-4 mt-8">
                  <button 
                    onClick={() => setActiveMediaIndex(prev => prev > 0 ? prev - 1 : project.gallery_images.length - 1)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-amber-500 hover:text-amber-500 transition"
                  >
                    <ChevronLeft />
                  </button>
                  <span className="text-white/60 font-medium tracking-widest text-sm">
                    {activeMediaIndex + 1} / {project.gallery_images.length}
                  </span>
                  <button 
                    onClick={() => setActiveMediaIndex(prev => prev < project.gallery_images.length - 1 ? prev + 1 : 0)}
                    className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white hover:border-amber-500 hover:text-amber-500 transition"
                  >
                    <ChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="p-8 md:p-12 max-w-4xl mx-auto">
              {project.description ? (
                <div className="prose prose-invert prose-lg max-w-none">
                  <p className="text-white/80 leading-relaxed font-light text-lg md:text-xl">
                    {project.description}
                  </p>
                </div>
              ) : (
                <div className="text-center py-20 text-white/40">
                  No detailed description provided for this project.
                </div>
              )}
            </div>
          )}

        </div>
      </div>
    </motion.div>
  );
}
